import React, { useCallback, useEffect, useRef, useState } from 'react'
import tripService from '../../../supabaseServices/TripService'
import userService from '../../../supabaseServices/UserService'
import { ACTIVITIES_TABLE_CONFIG } from './activityTableConfig'
import TableComponent from '../../../components/Tables'
import styles from '../TripsPage.module.scss'
import DefaultButton from '../../../components/Shared/Buttons/DefaultButton'
import { Paper } from '@mui/material'
import { useHistory, useParams } from 'react-router-dom'
import Tooltip from '@mui/material/Tooltip'
import { defaultFormatDate } from '../../../utils/format'
import DeleteContent from '../../../components/Shared/Modals/DeleteContent'
import CustomModal from '../../../components/Shared/Modals/CustomModal'
import { setSnackbar } from '../../../store/snackbar/actions'
import activityService from '../../../supabaseServices/ActivityService'
import { useDispatch } from 'react-redux'
import { tripActivitiesMenuConfig } from './tripActivitiesMenuConfig'
import authService from '../../../supabaseServices/AuthService'
import { ACTIVITIES_USERS_TABLE_CONFIG } from './activityUsersTableConfig'
import { ACTIVITIES_ACCOMMODATIONS_TABLE_CONFIG } from './activityAccommodationsTableConfig'
import TripMap from './TripMap'

interface Activity {
  id: number
  uuid: string
  start_time: string
  end_time: string
  location_name: string
  category: string
  name?: string
  price?: number
}

interface UserProfile {
  uuid: string
  first_name: string
  last_name: string
  email: string
}

function TripDetailsPage() {
  const dispatch = useDispatch()
  const { uuid } = useParams<{ uuid: string }>()
  const history = useHistory()
  const [activities, setActivities] = useState<Activity[]>([])
  const [users, setUsers] = useState<UserProfile[]>([])
  const [accommodations, setAccommodations] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedPage, setSelectedPage] = useState(1)
  const [tripEndDate, setTripEndDate] = useState<string | null>(null)
  const [tripData, setTripData] = useState<any | undefined>(undefined)
  const [showDeleteActivityModal, setShowDeleteActivityModal] = useState(false)
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null)
  const [selectedMapMarker, setSelectedMapMarker] = useState<string | null>(null)
  const mapRef = useRef<HTMLDivElement>(null)

  const onPressModalClose = useCallback(() => {
    setSelectedActivity(null)
    setShowDeleteActivityModal(false)
  }, [])

  const fetchTripDetails = async () => {
    try {
      const activitiesData = await tripService.getActivitiesByTripUUID(uuid)
      setActivities(activitiesData.data || [])
    } catch (e) {
      console.error('Failed to fetch trip details:', e)
    }
  }

  const fetchUserByTrip = async () => {
    try {
      const tripUsersResult = await tripService.getTripUsersByTripUUID(uuid)

      const userProfiles: UserProfile[] = (
        await Promise.all(
          (tripUsersResult.data || []).map(async (user: any) => {
            return await userService.getUserByUUID(user.user_uuid)
          }),
        )
      ).filter((profile): profile is UserProfile => profile !== null)

      setUsers(userProfiles)
    } catch (e) {
      console.error('Failed to fetch users by trip:', e)
    }
  }

  const fetchAccommodationsByTripUUID = async () => {
    try {
      const accommodationsResult = await tripService.getAccommodationsByTripUUID(uuid)
      setAccommodations(accommodationsResult.data || [])
    } catch (e) {
      console.error('Failed to fetch accommodations by trip:', e)
    }
  }

  const fetchTripData = async () => {
    try {
      const trip = await tripService.getTripByUUID(uuid)
      if (trip) {
        setTripData(trip)
        if (trip.end_date) {
          setTripEndDate(trip.end_date)
        }
      }
    } catch (error) {
      console.error('Error fetching trip data:', error)
    }
  }

  const loadAllData = async () => {
    setLoading(true)
    await Promise.all([
      fetchTripData(),
      fetchTripDetails(),
      fetchUserByTrip(),
      fetchAccommodationsByTripUUID(),
    ])
    setLoading(false)
  }

  useEffect(() => {
    loadAllData()
  }, [uuid])

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setSelectedPage(page)
  }

  if (loading) {
    return <p>Loading...</p>
  }

  const onCreateActivity = () => {
    history.push(`/trips/${uuid}/create-activity`)
  }

  const confirmDeleteActivity = async () => {
    try {
      const profile = await authService.getUserProfile()
      setShowDeleteActivityModal(false)

      if (!selectedActivity) {
        return console.error('No selected activity')
      }

      await activityService.deleteActivity(selectedActivity.id, profile.uuid)

      await fetchTripDetails()
      dispatch(
        setSnackbar({
          open: true,
          severity: 'success',
          message: `Activity ${selectedActivity.name} was successfully deleted!`,
        }),
      )
      setSelectedActivity(null)
    } catch (error) {
      console.error(error)
    }
  }

  const deleteActivity = (activity: Activity | null) => {
    setSelectedActivity(activity)
    setShowDeleteActivityModal(true)
  }

  const editActivity = (activity: Activity | null) => {
    if (activity) {
      history.push({
        pathname: `/trips/${uuid}/edit-activity`,
        state: { activity },
      })
    }
  }

  const isTripEnded = tripEndDate ? new Date(tripEndDate) < new Date() : false

  const handleActivityRowClick = (row: Activity) => {
    setSelectedMapMarker(`act-${row.uuid}`)
  }

  const handleAccommodationRowClick = (row: { uuid: string }) => {
    setSelectedMapMarker(`acc-${row.uuid}`)
  }

  const onCreateAccommodation = () => {
    history.push(`/trips/${uuid}/create-accommodation`)
  }

  return (
    <div className={styles.tripDetailsContainer}>
      <CustomModal
        maxWidth="sm"
        title="Delete Trip"
        closeModal={onPressModalClose}
        isVisible={showDeleteActivityModal}
        children={
          <DeleteContent
            confirmButtonColor="red"
            onPressConfirm={confirmDeleteActivity}
            onPressClose={onPressModalClose}
            text="Are you sure that you want to delete this activity?"
          />
        }
      />
      {/* Left Panel - Scrollable Content */}
      <div className={styles.tripDetailsContent}>
        <div className={styles.mainWrapper}>
          <div className={styles.headerWrapper}>
            <div className={styles.headerLeft}>
              <h1 className={styles.titleText}>Trip Details</h1>
            </div>
            {tripData && (
              <div className={styles.papersWrap}>
                <div className={styles.paperItem}>
                  Trip name
                  <Paper elevation={3}>
                    <div className={styles.paper}>{tripData.trip_name}</div>
                  </Paper>
                </div>
                <div className={styles.paperItem}>
                  Location
                  <Paper elevation={3}>
                    <div className={styles.paper}>{tripData.location}</div>
                  </Paper>
                </div>
                <div className={styles.paperItem}>
                  Start Date
                  <Paper elevation={3}>
                    <div className={styles.paper}>{defaultFormatDate(tripData.start_date)}</div>
                  </Paper>
                </div>
                <div className={styles.paperItem}>
                  End Date
                  <Paper elevation={3}>
                    <div className={styles.paper}>{defaultFormatDate(tripData.end_date)}</div>
                  </Paper>
                </div>
                {tripData.join_code && (
                  <div className={styles.paperItem}>
                    Join Code
                    <Paper elevation={3}>
                      <div className={styles.paper} style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                        {tripData.join_code}
                      </div>
                    </Paper>
                  </div>
                )}
                <DefaultButton
                  onClick={() => history.push(`/trips/${uuid}/edit`)}
                  outlined={true}
                  title={'Edit Trip'}
                />
              </div>
            )}
            <Tooltip title={isTripEnded ? 'The trip is over' : ''} placement="left-start">
              <span>
                <DefaultButton
                  onClick={onCreateActivity}
                  disabled={isTripEnded}
                  title={'Create activity'}
                />
              </span>
            </Tooltip>
          </div>

          {/* Organizer Info Section */}
          {tripData?.profiles && (
            <div className={styles.trip_users}>
              <h2>
                <b>Organizer</b>
              </h2>
              <Paper elevation={2} style={{ padding: '16px', maxWidth: '400px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <strong style={{ minWidth: '80px', color: '#666' }}>Name:</strong>
                    <span>
                      {tripData.profiles.first_name || ''} {tripData.profiles.last_name || ''}
                    </span>
                  </div>
                  {tripData.profiles.email && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <strong style={{ minWidth: '80px', color: '#666' }}>Email:</strong>
                      <a href={`mailto:${tripData.profiles.email}`}>{tripData.profiles.email}</a>
                    </div>
                  )}
                  {tripData.profiles.phone && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <strong style={{ minWidth: '80px', color: '#666' }}>Phone:</strong>
                      <a href={`tel:${tripData.profiles.phone}`}>{tripData.profiles.phone}</a>
                    </div>
                  )}
                </div>
              </Paper>
            </div>
          )}

          {/* Activities Section */}
          <div className={styles.trip_users}>
            <h2>
              <b>Activities</b>
            </h2>
            {!activities.length ? (
              <p className={styles.notFound}>No activities found</p>
            ) : (
              <TableComponent
                data={activities}
                configTable={ACTIVITIES_TABLE_CONFIG}
                handleChangePage={handlePageChange}
                selectedPage={selectedPage}
                maxPageNumbers={1}
                rowMenuConfig={({ row }) =>
                  tripActivitiesMenuConfig({ row, deleteActivity, editActivity })
                }
                paginated={true}
                rowClickable={true}
                onRowClick={handleActivityRowClick}
              />
            )}
          </div>

          {/* Trip Users Section */}
          <div className={styles.trip_users}>
            <h2>
              <b>Trip Users</b>
            </h2>
            {users.length > 0 ? (
              <TableComponent
                data={users}
                configTable={ACTIVITIES_USERS_TABLE_CONFIG}
                selectedPage={selectedPage}
                handleChangePage={handlePageChange}
                maxPageNumbers={1}
                rowClickable={false}
                paginated={false}
              />
            ) : (
              <p className={styles.notFound}>No users on trip</p>
            )}
          </div>

          {/* Accommodations Section */}
          <div className={styles.trip_users}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ margin: 0 }}>
                <b>Accommodations</b>
              </h2>
              <Tooltip title={isTripEnded ? 'The trip is over' : ''} placement="left-start">
                <span>
                  <DefaultButton
                    onClick={onCreateAccommodation}
                    disabled={isTripEnded}
                    title={'Add Accommodation'}
                  />
                </span>
              </Tooltip>
            </div>
            {accommodations.length > 0 ? (
              <TableComponent
                data={accommodations}
                configTable={ACTIVITIES_ACCOMMODATIONS_TABLE_CONFIG}
                selectedPage={selectedPage}
                handleChangePage={handlePageChange}
                maxPageNumbers={1}
                rowClickable={true}
                onRowClick={handleAccommodationRowClick}
                paginated={false}
              />
            ) : (
              <p className={styles.notFound}>No accommodations</p>
            )}
          </div>
        </div>
      </div>

      {/* Right Panel - Sticky Map */}
      <div ref={mapRef} className={styles.tripDetailsMap}>
        <TripMap
          activities={activities}
          accommodations={accommodations}
          selectedMarker={selectedMapMarker}
          onSelectMarker={setSelectedMapMarker}
        />
      </div>
    </div>
  )
}

export default TripDetailsPage
